<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {useAuthService} from "~/composables/useAuthService";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {endpoints} from "~/network/endpoints.js";
import {useMeetFormat} from "~/composables/useMeetFormat";
import {usePaymentsDisplay} from "~/composables/usePaymentsDisplay";
import {
  entryFeeJoinBlocked,
  entryFeeJoinFields,
  entryFeeJoinTerms,
  entryFeeJoinTickLabel,
  isEntryFeeTermsChanged,
  joinCancellationLine,
  joinPayeeLine,
  requestToJoinPaidLabel,
} from "~/composables/usePayingHonestly.js";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  namespace: {
    type: String,
    default: 'clubs',
    required: true,
  },
});

const loading = ref(false);
const errors = ref({});
const dialog = ref(false);
const message = ref(null);
const currentRequest = ref(null);
const isPartOfEntity = ref(true);

const {user, isSuperUser, isAuthenticated} = useAuthService();
const {httpRequest} = useHttpRequest();
const type = computed(() => {
  if (props.namespace === 'clubs') {
    return 'club';
  } else if (props.namespace === 'ladders') {
    return 'ladder';
  } else if (props.namespace === 'leagueCycles') {
    return 'box league cycle';
  }
});
const currentRequestId = computed(() => (currentRequest.value ? currentRequest.value.id : null));
const requestType = computed(() => {
  if (!user.value.id) {
    return 'none';
  }
  if (isPartOfEntity.value) {
    return 'member'; // ALREADY A MEMBER
  } else if (currentRequest.value != null) {
    return 'cancel';
  }
  return 'request'; // NOT A MEMBER ABLE TO REQUEST
});

const getCurrentRequest = async () => {
  if (!isAuthenticated.value) {
    return;
  }
  loading.value = true;
  try {
    const data = {
      filters: {
        clubIds: props.namespace === 'clubs' ? [props.id] : null,
        ladderIds: props.namespace === 'ladders' ? [props.id] : null,
        leagueCycleIds: props.namespace === 'leagueCycles' ? [props.id] : null,
        userIds: [user.value.id],
      },
    };
    const res = await httpRequest(endpoints.accessApplications.getItems, data);
    currentRequest.value = res.items[0];
  } catch (e) {
    errors.value = e;
  } finally {
    loading.value = false;
  }
};

const checkForExisting = async () => {
  if (!user.value.playerId) {
    return;
  }
  loading.value = true;
  try {
    const data = {
      filters: {
        playerIds: [user.value.playerId],
        ids: [props.id],
      },
    };
    let endpoint = endpoints.clubs.getItemCount;
    if (props.namespace === 'ladders') {
      endpoint = endpoints.ladders.getItemCount;
    } else if (props.namespace === 'leagueCycles') {
      endpoint = endpoints.leagueCycles.getItemCount;
    }
    const res = await httpRequest(endpoint, data);
    isPartOfEntity.value = res.count > 0;
  } catch (e) {
    errors.value = e;
  } finally {
    loading.value = false;
  }
};

const request = async (data) => {
  loading.value = true;
  // Join and pay (28 Sep 2026): a paid competition's request carries the entry
  // fee acceptance, read before anything reloads. Nothing is added when the
  // detail sends no terms (an older API), which keeps today's request.
  const feeFields = entryFeeJoinFields({
    asksTerms: requestTerms.value.asksTerms,
    ticked: entryFeeTicked.value,
    termsVersion: requestTerms.value.termsVersion,
  });
  try {
    const res = await httpRequest(endpoints.accessApplications.addItem, {...data, ...feeFields});
    currentRequest.value = res;
    dialog.value = false;
  } catch (e) {
    errors.value = e;
    // The terms moved on while the dialog was open: load them again and ask for a new tick.
    if (isEntryFeeTermsChanged(e)) {
      entryFeeTicked.value = false;
      await getItem(props.id);
    }
  } finally {
    loading.value = false;
  }
};

const cancelRequest = async () => {
  loading.value = true;
  try {
    await httpRequest(endpoints.accessApplications.deleteItem(currentRequestId.value), {});
    currentRequest.value = null;
    await checkForExisting();
  } catch (e) {
    errors.value = e;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  checkForExisting();
  getCurrentRequest();
});

const {item, getItem, itemErrors, itemPending} = useCrudApi("api/v2/" + props.namespace);

watch(dialog, (newVal) => {
  if (newVal) {
    entryFeeTicked.value = false;
    getItem(props.id);
  }
});

// ── Entry fee: the request names the fee and the member ticks its terms ──
// (CCRs reg 14 and 36; nothing is owed until an admin accepts.)
const {termsAsHtml} = useMeetFormat();
const {money} = usePaymentsDisplay();
const requestTerms = computed(() => entryFeeJoinTerms(item.value));
const feeAmount = computed(() => money(item.value?.entryFeeMinor, item.value?.entryFeeCurrency));
const entryFeeTicked = ref(false);
// The club as seller (CCRs reg 13) and the API's cancellation line above the
// tick (reg 13 / Sch 2); both absent with no fee or from an older API.
const requestPayee = computed(() => joinPayeeLine(item.value));
const requestCancellation = computed(() => joinCancellationLine(item.value));
const sendLabel = computed(() => requestTerms.value.asksTerms ? requestToJoinPaidLabel(feeAmount.value) : 'Send request');

</script>
<template>
  <div class="mt-4 pa-3" style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;" v-if="!isPartOfEntity">
    <div class="d-flex">
      <div class="flex-fill">
        <slot>
          {{
            requestType === 'request'
                ? 'You are not a member of this ' + type
                : 'A request has been sent to admins of this ' + type
          }}
        </slot>
      </div>
      <v-btn
          class="ml-2"
          :loading="loading"
          v-if="requestType === 'request'"
          color="primary"
          size="small"
          @click="dialog = true"
      >
        Request to join
      </v-btn>
      <v-btn
          class="ml-2"
          :loading="loading"
          v-if="requestType === 'cancel'"
          color="error"
          size="small"
          @click="cancelRequest"
      >
        Cancel request
      </v-btn>
      <crud-form-dialog
          :item-pending="itemPending"
          :btn-text="sendLabel"
          :disabled="entryFeeJoinBlocked({asksTerms: requestTerms.asksTerms, ticked: entryFeeTicked})"
          :form-pending="loading" :save-action="() => request({
                    message: message,
                    clubId: namespace === 'clubs' ? item.id : item.clubId,
                    ladderId: namespace === 'ladders' ? id : null,
                    leagueCycleId: namespace === 'leagueCycles' ? id : null
                  })" create-title="Request access" action="create" v-model:dialog="dialog">
        <template v-if="!itemPending">
          <div class="mb-4 pa-3" style="background: rgb(var(--v-theme-light)); border:1px solid rgb(var(--v-theme-borderLight)); border-radius:10px;">
            You are requesting to join {{ item.name }}. This will be sent to the club admin for
            approval. {{
              namespace !== 'clubs' && user && user.memberOf.indexOf(item.clubId) === -1
                  ? "Upon acceptance you will also become a member of " + item.clubName
                  : ""
            }}
          </div>
          <div class="tl-label mb-2">Leave an optional message</div>
          <v-textarea
              class="mb-4"
              v-model="message"
              hint="This will be sent to the club admin"
              persistent-hint
              :error-messages="errors.message"
              :errors="errors.message != null"
          />
          <div v-if="requestTerms.asksTerms" class="mb-4">
            <div class="tl-label">Entry fee terms</div>
            <p v-if="requestPayee" class="ra-line" data-testid="request-entry-fee-payee">{{ requestPayee }}</p>
            <div class="ra-terms-box" v-html="termsAsHtml(requestTerms.termsHtml)"/>
            <p v-if="requestCancellation" class="ra-line" data-testid="request-entry-fee-cancellation">{{ requestCancellation }}</p>
            <v-checkbox v-model="entryFeeTicked" density="compact" hide-details class="mt-2">
              <template #label>
                <span class="ra-terms-tick">{{ entryFeeJoinTickLabel(feeAmount) }}</span>
              </template>
            </v-checkbox>
          </div>
          <crud-error-display :errors="errors"></crud-error-display>
        </template>
      </crud-form-dialog>
    </div>
  </div>
</template>

<style scoped>
.ra-terms-box {
  max-height: 200px;
  overflow-y: auto;
  font-size: 12.5px;
  line-height: 1.45;
  background: rgb(var(--v-theme-light));
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 4px;
}

.ra-terms-tick {
  font-size: 13px;
}

.ra-line {
  font-size: 12.5px;
  line-height: 1.45;
  margin-top: 8px;
}
</style>
